import * as storage from '../storage/interface';

export const LOCALE_KEY = 'preferredLocale';
export const SESSION_KEY = 'session';
export const SCALE_KEY = 'scale';
export const THEME_KEY = 'preferredTheme';
/**
 * Backs the "Synced X ago" label on the Settings screen.
 *
 * Two fetches write it, per Requirement 2.5:
 *  - `useKitchenDisplay`'s device fetch, which returns this device's own
 *    settings (mode, printer, printerProfiles, timerMilestones,
 *    layoutStrategy, notificationConfig, ...) — i.e. almost everything the
 *    Settings screen displays. Runs once at app boot.
 *  - `useFetchSettings`, which returns org data (order types, sales channels,
 *    categories, org timings). None of it appears on the Settings screen, but
 *    it is what the refresh button re-runs.
 *
 * Both write on success only, so a failed fetch leaves the previous timestamp
 * rather than falsely reporting a fresh sync.
 */
export const LAST_SETTINGS_SYNCED_AT_KEY = 'lastSettingsSyncedAt';
import {
  App,
  Device,
  KitchenDisplay,
  Organization,
  Store,
  Venue,
  Section,
  PrinterProfile,
} from '@oolio-group/domain';
import { Scale, Theme } from '../common/types';

interface SortingPreference {
  label: string;
  value: string;
}

export interface Session {
  authorized: boolean;
  token?: string;
  refreshToken?: string;
  expiredDate?: number;
  currentVenue?: Partial<Venue>;
  currentStore?: Partial<Store>;
  currentOrganization?: Partial<Organization>;
  kitchenDisplay?: Partial<KitchenDisplay>;
  device?: Partial<Device>;
  activeApp?: App;
  audioToggle?: boolean;
  localeCode?: string;
  rebumpToggle?: boolean;
  sortingPref?: SortingPreference;
  sections?: Partial<Section>[];
  enableSeatManagement?: boolean;
  allPrinterProfiles?: PrinterProfile[];
}

export async function getSession(): Promise<Session | undefined> {
  try {
    const session = await storage.getItem<Session>(SESSION_KEY);
    return session || { authorized: false };
  } catch (e) {}

  return undefined;
}

export async function setSession(data: Session) {
  try {
    await storage.setItem(SESSION_KEY, data);
  } catch (e) {}
}

export async function getScale(): Promise<Scale | undefined> {
  try {
    const session = await storage.getItem<Scale>(SCALE_KEY);
    return session || Scale.MEDIUM;
  } catch (e) {}

  return undefined;
}

export async function setScale(data: Scale) {
  try {
    storage.setItem(SCALE_KEY, data);
  } catch (e) {}
}

export async function getTheme(): Promise<Theme | undefined> {
  try {
    const session = await storage.getItem<Theme>(THEME_KEY);
    return session || Theme.LIGHT;
  } catch (e) {}

  return undefined;
}

export async function setTheme(data: Theme) {
  try {
    storage.setItem(THEME_KEY, data);
  } catch (e) {}
}

export async function getLastSettingsSyncedAt(): Promise<number | undefined> {
  try {
    return await storage.getItem<number>(LAST_SETTINGS_SYNCED_AT_KEY);
  } catch (e) {}

  return undefined;
}

export async function setLastSettingsSyncedAt(timestamp: number) {
  try {
    await storage.setItem(LAST_SETTINGS_SYNCED_AT_KEY, timestamp);
  } catch (e) {}
}
